import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";

/**
 * How many candidacies arrived on each tab since the broker was last on it.
 *
 * The screen never reloads the list by itself, so the only thing it can say on
 * its own is "something came in over there". That claim is made by comparing
 * the counts per phase the API reports now against the counts this browser last
 * showed this broker, which is the only thing stored.
 *
 * Counts, not identities. Asking which candidacies arrived would mean fetching
 * the list, which is the expensive call this whole mechanism exists to avoid:
 * about 5 kB per candidacy and four extra queries, against about 90 bytes for
 * the counts.
 */
export type TCandidacyTabId = EIntakeStep | "archived";

/** Keyed by step number, exactly as `GET /applications/count-steps` returns it. */
export type TStepCounts = Record<string, number>;

export interface INewCandidaciesState {
  /** The counts this browser last showed, per step. */
  seenCounts: TStepCounts;
  /** Epoch milliseconds, for pruning the properties a broker no longer visits. */
  updatedAt: number;
}

const STORAGE_PREFIX = "rentio.candidacies.seen.";

/**
 * Enough for any broker's working set, and small enough that the pruning below
 * stays cheap. At roughly 250 bytes a property this is about 50 kB against a
 * budget of some 5 MB.
 */
const MAX_STORED_PROPERTIES = 200;

const storageKey = (propertyId: number) => `${STORAGE_PREFIX}${propertyId}`;

/**
 * Every read and write goes through these two. Storage throws rather than
 * returns null in a private window and under some corporate policies, and this
 * feature is a convenience: it has to disappear quietly rather than take the
 * candidacies screen down with it.
 */
const readRaw = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Full, blocked or unavailable. Nothing to do and nothing worth saying.
  }
};

export const readSeenCounts = (
  propertyId: number,
): INewCandidaciesState | undefined => {
  const raw = readRaw(storageKey(propertyId));
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.seenCounts) {
      return undefined;
    }

    // A stored baseline with no steps in it is not a baseline. Browsers that
    // ran the version which invented one hold exactly that, and reading it
    // back would keep announcing every tab's whole contents as new forever.
    // Treated as a first visit, which re-seeds from the next answer and heals
    // the stored value.
    if (Object.keys(parsed.seenCounts).length === 0) {
      return undefined;
    }

    return parsed as INewCandidaciesState;
  } catch {
    // Written by an older version of this code, or corrupted. Treated as a
    // first visit, which shows no numbers rather than wrong ones.
    return undefined;
  }
};

/**
 * One key per property, so opening a property reads its own entry and never
 * parses the other 199.
 */
export const writeSeenCounts = (propertyId: number, counts: TStepCounts) => {
  const state: INewCandidaciesState = {
    seenCounts: counts,
    updatedAt: Date.now(),
  };

  writeRaw(storageKey(propertyId), JSON.stringify(state));
  pruneStoredProperties();
};

/**
 * Forget what this browser last showed for a property, so the next answer from
 * the API becomes the new baseline and no number is shown in between. Used when
 * the broker's own action has moved candidacies between tabs and the remembered
 * counts describe the world before it.
 */
export const clearSeenCounts = (propertyId: number) => {
  try {
    window.localStorage.removeItem(storageKey(propertyId));
  } catch {
    // Same as writing: not worth failing over.
  }
};

/**
 * Keeps the most recently visited properties and drops the rest, so a broker who
 * works hundreds of properties over years does not slowly fill the origin's
 * storage. Dropping an entry costs that property its numbers once.
 */
export const pruneStoredProperties = () => {
  let keys: string[];

  try {
    keys = Object.keys(window.localStorage).filter(key =>
      key.startsWith(STORAGE_PREFIX),
    );
  } catch {
    return;
  }

  if (keys.length <= MAX_STORED_PROPERTIES) {
    return;
  }

  const byAge = keys
    .map(key => {
      const raw = readRaw(key);
      let updatedAt = 0;

      try {
        updatedAt = raw ? JSON.parse(raw).updatedAt || 0 : 0;
      } catch {
        updatedAt = 0;
      }

      return { key, updatedAt };
    })
    .sort((a, b) => a.updatedAt - b.updatedAt);

  byAge.slice(0, byAge.length - MAX_STORED_PROPERTIES).forEach(({ key }) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Same as writing: not worth failing over.
    }
  });
};

/**
 * Arrivals only, per tab.
 *
 * A tab whose count went down had a candidacy moved out of it by a colleague,
 * which raises nothing: this answers "did something come in since I was last
 * here", not "is my screen in sync". Without a stored baseline nothing is new,
 * so a broker opening a property for the first time never meets a wall of
 * numbers.
 */
export const getNewCountsPerTab = (
  currentCounts: TStepCounts | undefined,
  seenCounts: TStepCounts | undefined,
): Record<string, number> => {
  if (!currentCounts || !seenCounts) {
    return {};
  }

  const result: Record<string, number> = {};

  Object.keys(currentCounts).forEach(step => {
    const arrived = currentCounts[step] - (seenCounts[step] ?? 0);
    if (arrived > 0) {
      result[step] = arrived;
    }
  });

  return result;
};

/**
 * The broker has now seen this tab, so its own count is caught up while every
 * other tab keeps whatever it had. Marking all of them would clear numbers for
 * tabs the broker has still not looked at.
 */
export const withTabMarkedSeen = (
  seenCounts: TStepCounts | undefined,
  currentCounts: TStepCounts | undefined,
  tabId: TCandidacyTabId,
): TStepCounts | undefined => {
  // No baseline yet means the broker has not been given one, not that every
  // tab stood at zero. Marking a tab seen at this point used to invent a
  // baseline holding only that tab, and every other tab then compared itself
  // against nothing and announced its entire contents as new. Seeding is the
  // job of the effect that owns it; this function only ever catches one tab up
  // within a baseline that already exists.
  if (!seenCounts || !currentCounts || tabId === "archived") {
    return seenCounts;
  }

  return { ...seenCounts, [`${tabId}`]: currentCounts[`${tabId}`] ?? 0 };
};

/** Same steps, same numbers. Key order is not meaningful here. */
export const countsEqual = (
  a: TStepCounts | undefined,
  b: TStepCounts | undefined,
): boolean => {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  const steps = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...steps].every(step => (a[step] ?? 0) === (b[step] ?? 0));
};

/**
 * Whether the counts in hand may become the new baseline.
 *
 * After the broker moves a candidacy themselves the baseline is thrown away,
 * because it describes the world before the move. The counts already in hand
 * describe that same old world: they were fetched before the move and the
 * fresh ones are still a round trip away. Seeding from them and then receiving
 * the real ones is what put a "+1" on the tab the broker had just filled, and
 * offered them their own candidacy as a new arrival.
 *
 * So the counts as they stood at the moment of the action are remembered, and
 * nothing is seeded until the numbers actually differ from them. A phase move
 * always changes at least one step's count, so the wait ends as soon as the
 * answer lands. If it never does, no baseline is written and no number is
 * shown, which is the right way round to fail.
 *
 * One consequence worth naming: a genuine arrival from a colleague that lands
 * in the same answer is adopted into the baseline instead of being announced.
 * Not announcing one real arrival is a smaller fault than announcing the
 * broker's own move back at them every single time.
 */
export const shouldSeedBaseline = (
  currentCounts: TStepCounts | undefined,
  seenCounts: TStepCounts | undefined,
  countsBeforeOwnAction: TStepCounts | undefined,
): boolean => {
  if (!currentCounts || seenCounts !== undefined) {
    return false;
  }

  if (!countsBeforeOwnAction) {
    return true;
  }

  return !countsEqual(currentCounts, countsBeforeOwnAction);
};

/** Which of the rows now on a tab were not on it before it was reloaded. */
export const getArrivedIds = (
  idsBefore: string[],
  idsAfter: string[],
): string[] => {
  const before = new Set(idsBefore);
  return idsAfter.filter(id => !before.has(id));
};
